import { PHASE_MIX, type BlockKind, type PlanBlockV2, type TopicProgressV2 } from './types'
import { BLOCK_MINS } from './types'
import type { Phase } from './phases'
import { dueTopics } from './spacing'

export interface SupplyInfo {
  ours: boolean
  lessonId: number | null
  lessonTitle: string | null
}

export type SupplyFn = (subject: string) => SupplyInfo

function hashSeed(iso: string): number {
  let h = 0
  for (let i = 0; i < iso.length; i++) h = (h * 31 + iso.charCodeAt(i)) >>> 0
  return h
}

function mixCounts(n: number, phase: Phase): { video: number; soru: number; tekrar: number } {
  const mix = PHASE_MIX[phase]
  let video = Math.round(n * mix.video)
  let tekrar = Math.max(n >= 3 ? 1 : 0, Math.round(n * mix.tekrar))
  if (video + tekrar > n) tekrar = Math.max(0, n - video)
  const soru = Math.max(0, n - video - tekrar)
  if (phase === 4) {
    tekrar = Math.round(n * 0.4)
    video = 0
  }
  return { video, soru, tekrar }
}

export interface QueueInput {
  date: string
  slots: number
  phase: Phase
  cycle: string[]
  supply: SupplyFn
  topics: TopicProgressV2[]
}

export function buildQueue(input: QueueInput): PlanBlockV2[] {
  const { date, slots, phase, cycle, supply, topics } = input
  if (slots <= 0) return []
  const counts = mixCounts(slots, phase)
  const due = dueTopics(topics, date)
  const offset = hashSeed(date) % Math.max(1, cycle.length)
  const blocks: PlanBlockV2[] = []
  let seq = 1
  let rot = 0
  const nextSubject = (): string => {
    for (let i = 0; i < cycle.length; i++) {
      const s = cycle[(rot + offset + i) % cycle.length]
      if (supply(s).ours) {
        rot += i + 1
        return s
      }
    }
    for (let i = 0; i < cycle.length; i++) {
      const s = cycle[(rot + offset + i) % cycle.length]
      rot += i + 1
      return s
    }
    rot += 1
    return cycle[(rot + offset) % cycle.length]
  }

  const push = (kind: BlockKind, subject: string, extra: Partial<PlanBlockV2> = {}) => {
    blocks.push({
      kind,
      subject,
      mins: BLOCK_MINS[kind],
      seq: seq++,
      source: extra.source || (supply(subject).ours ? 'ours' : 'generic'),
      ...extra,
    })
  }

  let tekrarLeft = counts.tekrar
  while (tekrarLeft > 0 && due.length > 0) {
    const t = due.shift() as TopicProgressV2
    push('tekrar', t.topic_key, { source: 'system', topicKey: t.topic_key })
    tekrarLeft--
  }
  while (tekrarLeft > 0) {
    push('tekrar', 'Haftanın konuları', { source: 'system', light: tekrarLeft > 1 })
    tekrarLeft--
  }

  const flowSubjects = cycle.filter((s) => supply(s).ours)
  let videoLeft = counts.video
  let soruLeft = counts.soru
  while (videoLeft > 0 || soruLeft > 0) {
    const subject = nextSubject()
    const info = supply(subject)
    if (videoLeft > 0 && soruLeft > 0 && (phase < 4 || flowSubjects.length === 0)) {
      const pairId = `p${date}-${seq}`
      if (info.ours) {
        push('video', subject, { lessonId: info.lessonId ?? undefined, lessonTitle: info.lessonTitle ?? undefined, pairId })
      } else {
        push('video', subject, { directive: `${subject} konu çalışması`, pairId })
      }
      videoLeft--
      push('soru', subject, { pairId })
      soruLeft--
    } else if (soruLeft > 0) {
      push('soru', subject)
      soruLeft--
    } else if (videoLeft > 0) {
      push('soru', subject)
      videoLeft--
    } else {
      break
    }
  }

  return blocks.slice(0, slots)
}
